import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { SidebarNavProvider } from '@helix/sidebar-nav';
import {
    Navbar,
    ClientSwitcher,
    NavbarAdditionalItems,
    NavbarHelpLink,
    NavbarUserMenu,
    NavbarBody,
    NavbarIconAction,
} from '@helix/navbar';
import { useCommonStore, useSearchStore } from '../../../store/store';
import { runningEnv } from '../../../providers/endpoint.provider';
import { tokenTypes } from '../../../constants/auth';
import { HelixIcon } from '@lwt-helix/helix-icon';
import { chevron_left } from '@lwt-helix/helix-icon/outlined';
import { useAuthStore } from '../../../store/auth/store';
import { appIdByAppName, isAgentMetrics, runningApp } from '../../../constants/app';
import { labelsByApp } from '../../../constants/appLabels';
import { routesToShowBackNavBar } from '../../../constants/nav';
import { useMarketDynamicsStore } from '../../../store/marketDynamics/store';
import { backNavbarTimeTitleRoutes, getBackNavbarConfig } from '../../helpers/backNav';
import { resolveNavbarUserMenu } from '../../helpers/nav';
import { getIsOfficeListBreakdown } from '../../../agent-production/agent-movement/helpers/breakdowns';
import { terms } from '../../../agent-production/agent-movement/constants';
import { isEqual, sortBy } from 'lodash-es';
import { searchRoutes } from '../../../constants';
import { OFFICE_BRKDWN_GROWTH_TAB_INDEX } from '../../../agent-production/constants/breakdowns';
import { usePlatformService } from '../../hooks/usePlatformService';

const UniversalNavBar = props => {
    const history = useHistory();
    const location = useLocation();
    const { userItems, helpItems, children } = props;
    const [userMenuProps, setUserMenuProps] = useState({});
    // Seeded with userItems (not []) so the menu isn't empty while platformService.
    // getNavbarUserMenuProps() is still resolving -- matches the pre-ticket behavior of
    // rendering userItems directly, for LWA users during that window.
    const [menuItems, setMenuItems] = useState(userItems || []);

    const {
        selectedBreakdownOffice,
        resetBreakdowns,
        setSelectedBreakdownTab,
        setSelectedOfficeListSearch,
        timeFrameTitle,
        loadedCriteriaOfficesIds,
    } = useSearchStore(state => ({
        selectedBreakdownOffice: state[state.selectedModule]?.breakdowns?.selectedBreakdownOffice ?? {},
        resetBreakdowns: state.resetBreakdowns,
        selectedModule: state.selectedModule,
        setSelectedBreakdownTab: state.setSelectedBreakdownTab,
        setSelectedOfficeListSearch: state.setSelectedOfficeListSearch,
        timeFrameTitle: state[state.selectedModule]?.timeFrameTitle?.mainTitle,
        loadedCriteriaOfficesIds: state[state.selectedModule]?.loadedSearchCriteria?.realEstateDatasourceIdsWithFilters?.find(mls =>
            mls.realEstateDatasourceId === state[state.selectedModule]?.selectedMls?.mlsId
        )?.idFiltering?.[0]?.idValues,
    }));

    const {
        backNavbarTitle,
        backLinkTitle,
        handleBackNavigation,
        officeLists,
        pendoDataLayers,
        setBackLinkTitle,
        setBackNavbarTitle
    } = useCommonStore(state => ({
        backNavbarTitle: state.backNavbar.title,
        backLinkTitle: state.backNavbar.backLinkTitle,
        handleBackNavigation: state.handleBackNavigation,
        officeLists: state.officeLists,
        pendoDataLayers: state.pendoDataLayers,
        setBackLinkTitle: state.setBackLinkTitle,
        setBackNavbarTitle: state.setBackNavbarTitle,
    }));

    const {
        selectedBreakdownPeriod,
        tableView,
    } = useMarketDynamicsStore(state => ({
        tableView: state.tableView,
        selectedBreakdownPeriod: state.selectedBreakdownPeriod,
    }));

    const {
        activeClientId,
        tokenType,
        tokenInfo,
        isLegacyUser,
    } = useAuthStore(state => ({
        activeClientId: state.activeClientId,
        tokenType: state.tokenType,
        tokenInfo: state.tokenInfo,
        isLegacyUser: state.isLegacyUser,
    }));
    const isLWAAuthenticated = tokenType === tokenTypes.LWA;
    const { data: platformService, isLoading: isPlatformServiceLoading } = usePlatformService(isLWAAuthenticated);
    const currentOfficeList = officeLists?.find(list => isEqual(sortBy(list.officeIds), sortBy(loadedCriteriaOfficesIds)));

    useEffect(() => {
        const checkPendoReady = () => {
            if (window?.pendo) {
                window.pendo.updateOptions(pendoDataLayers);
            } else {
                setTimeout(checkPendoReady, 50);
            }
        };
        checkPendoReady();
    }, [pendoDataLayers]);

    useEffect(() => {
        setSelectedOfficeListSearch(currentOfficeList);
        const officesListNavTitle = currentOfficeList?.name || terms.selectedOffices;
        const breakdownNavbarTitle = getIsOfficeListBreakdown(window.location.pathname)
            ? officesListNavTitle : selectedBreakdownOffice?.name;
        const backBtnTitle = location.state?.fromOfficeBreakdownAndGrowthTab
            && terms.officeBreakdownAndGrowth;
        const config = getBackNavbarConfig({
            route: location.pathname,
            title: backNavbarTimeTitleRoutes.includes(location.pathname) ? timeFrameTitle : breakdownNavbarTitle,
            backBtnTitle,
        });
        if (!config) return;
        setBackLinkTitle(config.backLinkTitle);
        const title = config.getBackNavbarTitle({ period: selectedBreakdownPeriod, tableView });
        if (title) setBackNavbarTitle(title);
    }, [location.pathname, timeFrameTitle, selectedBreakdownOffice?.name, currentOfficeList]);

    const handleBackBtn = () => {
        if (location.state?.fromOfficeBreakdownAndGrowthTab) {
            handleBackNavigation(location.pathname, history, searchRoutes.officeListBreakdown);
            setSelectedBreakdownTab(OFFICE_BRKDWN_GROWTH_TAB_INDEX);
            return;
        }
        handleBackNavigation(location.pathname, history);
        resetBreakdowns();
    };

    const showBackNavBar = routesToShowBackNavBar.includes(window.location.pathname);

    // App.js builds userItems inline on every render (a fresh array/onClick closures each
    // time), so depending on the array itself would re-run this effect -- and re-hit
    // platformService.getNavbarUserMenuProps() -- on every App re-render. Depend on the
    // labels instead; the effect body below still uses the current userItems value.
    const userItemLabels = userItems?.map(item => item.label).join('|');

    useEffect(() => {
        let cancelled = false;

        // The platform service builds the universal Sign Out item (and Manage Account) for
        // LWA users when there's an active token, then appends our own custom items after it.
        resolveNavbarUserMenu({ isLegacyUser, tokenInfo, platformService, userItems }).then(result => {
            if (cancelled) return;
            if (result) {
                setUserMenuProps(result.userMenuProps);
                setMenuItems(result.menuItems);
                return;
            }
            // menuItems falls back to userItems (e.g. "My Profile") rather than leaving the
            // menu empty -- that has clear precedent, the pre-ticket code always rendered
            // userItems directly. userMenuProps is left alone here: unlike menuItems, it
            // never had a userItems-based fallback before this ticket, and tokenInfo (the raw
            // decoded JWT) isn't guaranteed to match the shape platformService's own resolved
            // user object has, which is what NavbarUserMenu actually expects for LWA users.
            setMenuItems(userItems || []);
        });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- userItemLabels stands in for userItems, see comment above
    }, [isLegacyUser, tokenInfo, platformService, userItemLabels]);

    const BackNavBar = () => (
        <Navbar
            dataLwtId='xbmNav'
            location={location}
            hideLogo
            environment={runningEnv?.toLocaleLowerCase()}
            platformService={platformService}
            {...(isAgentMetrics && { activeClientId })}
        >
            <NavbarBody>
                <NavbarIconAction
                    onClick={handleBackBtn}
                    icon={
                        <HelixIcon
                            className='header-back-button'
                            icon={chevron_left}
                        />
                    }>
                    <span className='helix-brand-color'>{backLinkTitle}</span>
                </NavbarIconAction>
            </NavbarBody>
            <NavbarBody>
                <span className='helix-body--strong'>
                    {backNavbarTitle}
                </span>
            </NavbarBody>
            <NavbarAdditionalItems className='navbar-additonal-items'>
                <NavbarHelpLink menuItems={helpItems || []} hideHelpHeader='true' />
                <NavbarUserMenu menuItems={menuItems} user={userMenuProps} />
            </NavbarAdditionalItems>
        </Navbar>
    );

    return (
        <SidebarNavProvider
            activeClientId={activeClientId || undefined}
            environment={runningEnv?.toLowerCase() || 'dev'}
            applicationCode={appIdByAppName[runningApp] || 'xbm'}
            platformService={!isLegacyUser ? platformService : undefined}
            useMockData={false}
        >
            <div className={`helix-d-flex helix-flex-direction--column${isLegacyUser ? ' legacy-layout' : ''}`} style={{ height: '100vh' }}>
                {/* IMPORTANT: id="helix-application-header" is required, the SidebarNav CollapseButton uses createPortal targeting this element. */}
                <div id='helix-application-header' style={{ position: 'relative', flexShrink: 0, zIndex: 2 }}>
                    {showBackNavBar ? (
                        <BackNavBar />
                    ) : (
                        <Navbar
                            dataLwtId='xbmNav'
                            applicationCode={appIdByAppName[runningApp]}
                            applicationName={labelsByApp[runningApp]?.headerLabel}
                            environment={runningEnv?.toLocaleLowerCase() || 'dev'}
                            platformService={platformService}
                            style={{ width: '100%' }}
                        >
                            {platformService && !isPlatformServiceLoading && isLWAAuthenticated && (
                                <ClientSwitcher platformService={platformService} />
                            )}
                            <NavbarAdditionalItems className='navbar-additonal-items'>
                                <NavbarHelpLink menuItems={helpItems || []} hideHelpHeader='true' />
                                <NavbarUserMenu menuItems={menuItems} user={userMenuProps} />
                            </NavbarAdditionalItems>
                        </Navbar>
                    )}
                </div>
                {/* SidebarNav + content are rendered through the children (ApplicationLayout) */}
                <div className='helix-flex-1 helix-d-flex helix-overflow--hidden'>
                    {children}
                </div>
            </div>
        </SidebarNavProvider>
    );
};

UniversalNavBar.propTypes = {
    userItems: PropTypes.array,
    helpItems: PropTypes.array,
    children: PropTypes.node,
};

export default UniversalNavBar;
